import React, { useEffect, useRef, useState } from 'react'
import { Button, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Tooltip, Stack } from '@mui/material';
import TestData from './TestData';
import { useTranslation } from 'react-i18next';
import CommonGrid from '../../../Repository/CommonGrid';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../../../redux/api';
import ErrorModal from '../../../ItemProperties/Forms/FieldDataValidation/ErrorModal';
import LightTooltip from '../../../../commonComponents/LightTooltip';
import { customDecoding, customEncoding, stripTrailingWorkflowFieldSep } from '../../../../utils/utilFunctions';

const Stripping = ({ setHelpDocPath, handReplaceAndStrip, strips, updateLength, currTab, selectedNode }) => {
  const { t } = useTranslation();
  const { account } = useSelector((state) => state.queryParams);
  const dispatch = useDispatch();
  const currentRow = useRef(null);
  const containerRef = useRef(null);
  const [gridApi, setGridApi] = useState('');
  const [valueToStrip, setValueToStrip] = useState('');
  const [radioValue, setRadioValue] = useState(t('[VERBATIM]'));
  const [rowData, setRowData] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(1);
  const [testData, setTestData] = useState('');
  const [testResult, setTestResult] = useState('');
  const [testStrip, setTestStrip] = useState('');
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [rowDataLength, setRowDataLength] = useState(0);

  const handleErrorModal = () => {
    setErrorModal(false);
    setErrorMessage('');
  }

  const updatingData = (newRows) => {
    let stripping = "";
    for (let i = 0; i < (newRows?.length || 0); i++) {
      const rowValue = newRows[i]?.value?.trim(); // Ensure the value is trimmed and not null/undefined

      if (rowValue && rowValue.endsWith('(Reg Ex)')) {
        const val = rowValue.replace(/\s*\(\s*Reg\s*Ex\s*\)\s*$/, '');
        if (val) stripping += "%1D" + customEncoding(rowValue.replace(/\s*\(\s*Reg\s*Ex\s*\)\s*$/, '')) + "%1F" + "%1E";
      }
      else if (rowValue) {
        const val = rowValue.replace(/\s*\(\s*Verbatim\s*\)/, '');
        if (val) stripping += customEncoding(rowValue.replace(/\s*\(\s*Verbatim\s*\)/, '')) + "%1F" + "%1E";
      }
    }
    handReplaceAndStrip({ type: 'STRIP', strip: stripping });
  }

  const handleHelp = (value) => {
    switch (value) {
      case (value === t('[VERBATIM]') || t('[REG_EX]')):
        setHelpDocPath('regex.html')
        break;

      default:
        setHelpDocPath('regex.html')
        break;
    }
  }

  const handleInputChange = (name, value) => {
    let newValue = '';
    if (name === 'VAL_TO_STRIP') {
      newValue = `${value} ${radioValue}`;
    } else if (name === 'RADIO') {
      newValue = `${valueToStrip} ${value}`;
    }

    let updatedRowData = [];
    gridApi.forEachNode(node => {
      if (node?.data.id === currentRow?.current?.id) {
        const newNode = { ...node.data, value: newValue };
        setTestStrip(newValue);
        updatedRowData.push(newNode);
      } else {
        updatedRowData.push(node.data);
      }
    });
    updatingData(updatedRowData);
    setRowDataLength(updatedRowData?.length);
    updateLength({ type: 'STRIP', len: updatedRowData?.length });
    gridApi.setGridOption('rowData', updatedRowData)
  }

  const handleCopy = (info) => {
    const newRowData = [];
    let maxId = 0;

    info.api.forEachNode((node, index) => {
      const newNodeData = { ...node.data };
      if (newNodeData.id > maxId) {
        maxId = newNodeData.id;
      }
      newRowData.push(newNodeData);
    });

    if (currentRow.current) {
      const copiedRow = { ...currentRow.current, id: maxId + 1 };
      newRowData.push(copiedRow);
    }

    info.api.setGridOption('rowData', newRowData)
    updatingData(newRowData);
    setRowDataLength(newRowData?.length);
    updateLength({ type: 'STRIP', len: newRowData?.length });

    // Highlight the newly added row
    const newRowId = maxId + 1;
    setSelectedIndex(newRowId - 1)
    if (newRowId !== null) {
      const rowNode = info.api.getRowNode(newRowId - 1);
      if (rowNode) {
        currentRow.current = rowNode.data;
        handleDefaultValuesForFields();
        rowNode.setHighlighted(true);
      }
    }
  }

  const handleDelete = (info) => {
    const newRowData = [];
    info.api.forEachNode((node, index) => {
      if (node.data.id !== currentRow.current.id) {
        newRowData.push({ ...node.data, id: newRowData.length + 1 });
      }
    });
    info.api.setGridOption('rowData', newRowData)
    updatingData(newRowData);
    setRowDataLength(newRowData?.length);
    updateLength({ type: 'STRIP', len: newRowData?.length });
    const firstRowIndex = info.node.rowIndex > 0 ? currentRow?.current?.id - 1 : info.node.rowIndex === 0 && newRowData?.length ? 1 : null;
    if (firstRowIndex !== null) {
      const rowNode = info.api.getRowNode(firstRowIndex - 1);
      if (rowNode) {
        currentRow.current = rowNode?.data;
        info.api.redrawRows();
        handleDefaultValuesForFields();
      }
    }
  }

  const getTestData = (val) => {
    setTestData(val);
  }

  const createStripFortesting = () => {
    const strips = [];
    gridApi.forEachNode((node) => {
      let value = node.data.value?.replace(/\u001f/g, ' ') || '';
      
      if (value.includes('(Verbatim)')) {
        value = value.replace(/\s*\(\s*Verbatim\s*\)/, ' (Verbatim)');
      } else if (value.includes('(Reg Ex)')) {
        value = value.replace(/\s*\(\s*Reg\s*Ex\s*\)/, ' (Reg Ex)');
      }
      
      strips.push(value);
    })
    return strips?.join(',');
  }

  const recieveTestReqeust = (val) => {
    createStripFortesting()
    if (val) {
      const body = {
        testData: testData,
        strip: createStripFortesting()
      }
      testDataFromServer(body);
    }
  }

  const testDataFromServer = (data) => {
    setLoading(true);
    dispatch(api.endpoints.testStrippingData.initiate({
      account,
      data: data,
    },{forceRefetch: true})).then((res) => {
      if (res?.data?.current_state === 'SUCCESS') {
        setTestResult(res?.data?.data);
        setLoading(false);
      } else if (res?.data?.current_state === 'ERROR') {
        setErrorModal(true);
        setErrorMessage(res?.data?.message);
        setLoading(false);
      }
    })
  }

  const customClickHandlerOnAdd = (e, rowIndex) => {
    const newRowData = [];
    e.api.forEachNode((node) => {
      const newIndex = newRowData.length;
      newRowData.push({ ...node.data, id: newIndex });
    });
    const newData = { id: newRowData?.length, value: `${radioValue}` }
    setSelectedIndex(newData.id);
    currentRow.current = newData;
    setValueToStrip('');
    setRadioValue(t('[VERBATIM]'));
    setTestStrip(newData?.value);
    newRowData.push(newData);
    e.api.setGridOption('rowData', newRowData)
    setRowData(newRowData);
    setRowDataLength(newRowData?.length);
    updateLength({ type: 'STRIP', len: newRowData?.length });
  }

  const gridOptions = {
    columnDefs: [
      {
        addNewControl: true,
        addControlLabel: t('MNU_NEW_STRIP'),
        headerName: t('MNU_NEW_STRIP'),
        field: "value",
        editable: (params) => !params.data.value,
        cellRenderer: (info) => CustomCellRenderer(info),
        customClickHandlerOnAdd: customClickHandlerOnAdd,
        rowDrag: true
      }
    ],
    getRowStyle: (params) => {
      const rowData = params.data;
      if (rowData?.id === currentRow?.current?.id) {
        return { border: '1px solid black' };
      } else {
        return { opacity: '0.5' };
      }
    },
    rowDragManaged: true,
  };

  const handleRow = (options) => {
    if (options?.eventPath?.[0]?.classList?.[0]?.includes('DELETE_MUI')) return;
    if (options?.eventPath?.[0]?.classList?.[0]?.includes('FILE_COPY')) return;
    currentRow.current = options.data;
    setTestStrip(options.data.value);
    options.api.redrawRows();
    handleDefaultValuesForFields();
  };

  const scrollRows = () => {
    if (gridApi && selectedIndex) {
      const rowNode = gridApi.getRowNode(selectedIndex);
      if (rowNode) {
        rowNode.setHighlighted(true);
        gridApi.ensureIndexVisible(selectedIndex, 'start');
      }
    }
  };

  const CustomCellRenderer = (info) => {
    const { value, data } = info;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: '2rem',
        }}
      >
        {!value || /^\s*$/.test(value) ? (
          <span style={{ color: "#0000008A" }}>{t("MNU_NEW_STRIP")}</span>
        ) : (
          <div className='truncated-text' title={value}>{value}</div>
        )}
        {data?.key || data?.value ? (
          <div className="flex align-center">
            <LightTooltip
              title={'Copy Strip'}
              arrow>
              <span style={{ cursor: 'pointer' }} className="FILE_COPY" onClick={() => handleCopy(info)}></span>
            </LightTooltip>

            <LightTooltip
              title={'Delete Strip'}
              arrow>
              <span style={{ cursor: 'pointer' }} className="DELETE_MUI" onClick={() => handleDelete(info)}></span>
            </LightTooltip>
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };

  const handleDefaultValuesForFields = () => {
    if (currentRow?.current) {
      let veb;
      if (currentRow?.current.value.endsWith('(Reg Ex)')) {
        veb = currentRow?.current.value.replace(/\s*\(\s*Reg\s*Ex\s*\)\s*|\x1F/g, '');
        setRadioValue(t('[REG_EX]'));
      } else {
        veb = currentRow?.current.value.replace(/\s*\(\s*Verbatim\s*\)\s*|\x1F/g, '');
        setRadioValue(t('[VERBATIM]'));
      }
      setValueToStrip(veb);
    }
  };

  const generateDefault = (rows) => {
    let strip = '';
    rows?.forEach((row) => {
      if (row?.includes("%1D")) {
        const inner = row.replace(/^%1D/, '');
        strip += "%1D" + customEncoding(customDecoding(stripTrailingWorkflowFieldSep(inner))) + "%1F" + "%1E";
      } else {
        strip += customEncoding(customDecoding(stripTrailingWorkflowFieldSep(row))) + "%1F" + "%1E";
      }
    });
    handReplaceAndStrip({ type: 'STRIP', strip: strip });
  }


  const handleDefaultValues = () => {
    const updatedValues = [];
    if (strips?.length !== 0) {
      for (let i = 0; i < strips?.length; i++) {
        let id = i + 1;
        if (strips[i]?.startsWith('%1D')) {
          const inner = strips[i].replace(/^%1D/, '');
          updatedValues.push({ id, value: `${customDecoding(stripTrailingWorkflowFieldSep(inner))}${t('[REG_EX]')}` });
        } else {
          updatedValues.push({ id, value: `${customDecoding(stripTrailingWorkflowFieldSep(strips[i]))}${t('[VERBATIM]')}` });
        }
      }
    }

    if (updatedValues?.length !== 0) {
      generateDefault(strips);
      currentRow.current = updatedValues[0];
      setTestStrip(updatedValues[0]?.value)
      handleDefaultValuesForFields();
    }
    setRowDataLength(updatedValues?.length)
    updateLength({ type: 'STRIP', len: updatedValues?.length });
    setRowData(updatedValues)
  }

  const onRowDragEnd = e => {
    const nodes = [];
    e.api.forEachNode((node) => {
      nodes.push(node?.data);
    });
    setRowData(nodes);
    updatingData(nodes);
  };

  useEffect(() => {
    if (containerRef.current && currentRow.current) {
      const itemHeight = 40;
      const containerHeight = containerRef.current.clientHeight;
      const scrollTo = selectedIndex * itemHeight;
      if (scrollTo > containerHeight) {
        containerRef.current.scrollTop = scrollTo;
        setTimeout(() => {
          scrollRows();
        }, 0);
      }
    }
  }, [rowData, selectedIndex]);

  useEffect(() => {
    handleDefaultValues();
  }, [selectedNode?.key]);

  useEffect(() => {
    if (currTab === 2) {
      if (radioValue) {
        if (rowDataLength > 1) {
          if (radioValue === t('[VERBATIM]')) {
            setHelpDocPath('adv_stripping.html');
          } else {
            handleHelp(radioValue);
          }
        }
      }
    }
  }, [radioValue, rowDataLength, currTab])

  return (
    <>
      <div className='padding-16'>
        <Stack spacing={"15px"}>
          <div ref={containerRef}>
            <CommonGrid
              gridOptions={gridOptions}
              rowDragManaged={true}
              rowData={rowData}
              getGridAPI={(gridOptions, gridApi) => setGridApi(gridApi.api)}
              containerStyle={{ height: "30vh" }}
              gridProps={{
                onCellClicked: handleRow,
                onRowDragEnd: onRowDragEnd,
              }}
            />
          </div>
          {rowDataLength !== 0 && <>
            <FormControl className="width-100" size="small">
              <TextField
                variant="outlined"
                size="small"
                label={t("LBL_VAL_TO_STRIP")}
                type="text"
                className={"width-100"}
                name={"VAL_TO_STRIP"}
                value={valueToStrip}
                autoComplete="off"
                onChange={(e) => {
                  setValueToStrip(e.target.value)
                  handleInputChange('VAL_TO_STRIP', e.target.value);
                }}
              />
            </FormControl>

            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="female"
              name="radio-buttons-group"
              value={radioValue || ''}
              onClick={(e) => {
                setRadioValue(e.target.value);
                handleHelp(e.target.value);
                handleInputChange('RADIO', e.target.value);
              }}
            >
              <FormControlLabel value={t('[VERBATIM]')} control={<Radio />} label={t("BTN_USE_VAL_TO_REPLACE_VERBATIM")} />
              <FormControlLabel value={t('[REG_EX]')} control={<Radio />} label={t("BTN_USE_VAL_TO_REPLACE_EXPR")} />
            </RadioGroup>
          </>
          }

          <TestData testResult={testResult} getTestData={getTestData} type={''} sendTestRequest={recieveTestReqeust} isLoading={loading} />
        </Stack>
      </div>
      {errorModal && <ErrorModal
        errorModal={errorModal}
        handleErrorModal={handleErrorModal}
        errorMessage={errorMessage}></ErrorModal>}
    </>
  )
}

export default Stripping